/**
 * Module Studio (UPT-2079 lot L2, §4.2) — lecture d'une erreur des routes de
 * dossiers et de specs : 403 → `forbidden` (sauf un code que le contrat déclare lui-même en 403 :
 * `generator.stripe.platform_only`, UPT-2079 L5c) ; corps `{code, message}` dont le
 * code est une clé de `DESIGN_ERROR_STATUS` → ce code et ce `message` (raison
 * fixée par le serveur : `no-config`, `credential`, numéro de version…) ; pas
 * de réponse → `network` ; sinon `unknown`. Le `message` n'est jamais affiché
 * brut : seules les raisons connues passent par la table du §5.6.
 * Le dépôt de démonstration lève ses erreurs sous la même forme
 * `{ response: { status, data: { code, message } } }`.
 */
import { DESIGN_ERROR_STATUS, type DesignErrorCode } from '../data/contracts';

const referencePublicationErrors=['reference.publish.empty','reference.publish.accepted_required','reference.publish.local_required','reference.publish.catalog_required','reference.publish.invalid'] as const;
export type DesignUiErrorCode = DesignErrorCode | typeof referencePublicationErrors[number] | 'transaction.rollback_only' | 'design.schema_drift' | 'studio.permission.required' | 'forbidden' | 'network' | 'unknown';

export interface DesignUiError {
  remainingCount?: number;
  reference?: string;
  credentialName?: string;
  permission?: string;
  action?: string;
  code: DesignUiErrorCode;
  status: number | null;
  message: string | null;
}

const KNOWN_CODES = new Set<string>(Object.keys(DESIGN_ERROR_STATUS));

export const isDesignErrorCode = (code: string): code is DesignErrorCode => KNOWN_CODES.has(code);

/** Traduit une erreur axios, réseau ou de démonstration en code d'interface stable. */
export function readDesignError(error: unknown): DesignUiError {
  const candidate = error as {
    response?: { status?: number; data?: unknown };
    request?: unknown;
  } | null;
  const response = candidate?.response;
  if (response && typeof response.status === 'number') {
    const data = response.data;
    if (response.status === 500 && data && typeof data === 'object' && (data as { code?: unknown }).code === 'transaction.rollback_only') {
      const reference = (data as { reference?: unknown }).reference;
      return { code: 'transaction.rollback_only', status: 500, message: null,
        reference: typeof reference === 'string' && /^[0-9a-f-]{36}$/i.test(reference) ? reference : undefined };
    }
    if (response.status === 409 && data && typeof data === 'object' && (data as { code?: unknown }).code === 'design.schema_drift') {
      return { code: 'design.schema_drift', status: 409, message: null };
    }
    if (response.status === 403) {
      const named = data as { code?: unknown; permission?: unknown; action?: unknown } | null;
      if (named?.code === 'studio.permission.required' && typeof named.permission === 'string' && typeof named.action === 'string') {
        const parsed: DesignUiError = { code: 'studio.permission.required', status: 403, message: null, permission: named.permission, action: named.action };
        if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('studio-permission-denied', { detail: parsed }));
        return parsed;
      }
      // Les 403 de droits sont ceux de Spring Security (sans code). Seul un code que le contrat déclare en 403
      // (`generator.stripe.platform_only`) est lu ; tout autre corps reste un refus de droits, sans détail.
      const forbiddenCode = data && typeof data === 'object' ? (data as { code?: unknown }).code : undefined;
      if (typeof forbiddenCode === 'string' && isDesignErrorCode(forbiddenCode) && DESIGN_ERROR_STATUS[forbiddenCode] === 403) {
        return { code: forbiddenCode, status: 403, message: null };
      }
      return { code: 'forbidden', status: 403, message: null };
    }
    if (data && typeof data === 'object') {
      const raw = (data as { code?: unknown; message?: unknown }).code;
      if(response.status===409&&typeof raw==='string'&&referencePublicationErrors.some(code=>code===raw))return {code:raw as typeof referencePublicationErrors[number],status:409,message:null};
      if (typeof raw === 'string' && isDesignErrorCode(raw)) {
        const message = (data as { message?: unknown }).message;
        const name = (data as { credentialName?: unknown }).credentialName;
        const remaining = (data as { details?: { remainingCount?: unknown } }).details?.remainingCount;
        return { code: raw, status: response.status, message: typeof message === 'string' ? message : null,
          ...(typeof remaining === 'number' && Number.isSafeInteger(remaining) && remaining >= 0 ? { remainingCount: remaining } : {}),
          credentialName: typeof name === 'string' && /^AZURE_DEVOPS_PAT[A-Z0-9_]*$/.test(name) ? name : undefined };
      }
    }
    return { code: 'unknown', status: response.status, message: null };
  }
  if (candidate?.request) {
    return { code: 'network', status: null, message: null };
  }
  return { code: 'unknown', status: null, message: null };
}

/** Forme d'erreur du dépôt de démonstration (passe par `readDesignError` sans distinction). */
export const designError = (code: DesignErrorCode, message: string | null = null): Error =>
  Object.assign(new Error(code), {
    response: { status: DESIGN_ERROR_STATUS[code], data: { code, message } },
  });
